/**
 * userPublicCatalogStore — guest (unauthenticated) catalog slice (Zustand).
 *
 *   GET /api/public/catalog/books            GET /api/public/catalog/books/:id
 *   GET /api/public/catalog/courses          GET /api/public/catalog/courses/:idOrSlug
 *   GET /api/public/catalog/courses/:idOrSlug/reviews
 *   GET /api/public/catalog/courses/:idOrSlug/lessons/:lessonId/preview
 *   GET /api/public/catalog/journals         GET /api/public/catalog/journals/:id
 *   GET /api/public/catalog/resources        GET /api/public/catalog/resources/:type/:idOrSlug
 *   GET /api/public/catalog/categories
 *   GET /api/public/catalog/learning-paths   GET /api/public/catalog/learning-paths/:id
 *   GET /api/public/catalog/search
 *
 * These mirror the authenticated /api/learners/* catalog but are readable
 * without a session, so every request explicitly sends `token: ""` (no
 * Authorization header) instead of the stored bearer token.
 */
import { apiRequest, normalizeList } from "./userUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const userPublicCatalogInitialState = {
  publicTopics: [],
  publicTopicsLoading: false,
  publicTopicsError: null,
  publicDepartments: [],
  publicDepartmentsLoading: false,
  publicDepartmentsError: null,
  publicCategories: [],
  publicCategoriesLoading: false,
  publicCategoriesError: null,

  publicBooks: [],
  publicBooksLoading: false,
  publicBooksError: null,
  publicBooksTotal: 0,
  publicBooksPages: 1,

  publicBook: null,
  publicBookLoading: false,
  publicBookError: null,

  publicCourses: [],
  publicCoursesLoading: false,
  publicCoursesError: null,
  publicCoursesTotal: 0,
  publicCoursesPages: 1,

  publicTrendingCourses: [],
  publicTrendingCoursesLoading: false,
  publicTrendingCoursesError: null,

  publicCourse: null,
  publicCourseLoading: false,
  publicCourseError: null,

  publicCourseReviews: [],
  publicCourseReviewsLoading: false,
  publicCourseReviewsError: null,
  publicCourseReviewsTotal: 0,
  publicCourseReviewsPages: 1,

  publicJournals: [],
  publicJournalsLoading: false,
  publicJournalsError: null,
  publicJournalsTotal: 0,
  publicJournalsPages: 1,

  publicJournal: null,
  publicJournalLoading: false,
  publicJournalError: null,

  publicResources: [],
  publicResourcesLoading: false,
  publicResourcesError: null,
  publicResourcesTotal: 0,
  publicResourcesPages: 1,

  publicLearningPaths: [],
  publicLearningPathsLoading: false,
  publicLearningPathsError: null,
  publicLearningPathsTotal: 0,
  publicLearningPathsPages: 1,

  publicLearningPath: null,
  publicLearningPathLoading: false,
  publicLearningPathError: null,

  publicSearchResults: [],
  publicSearchLoading: false,
  publicSearchError: null,
  publicSearchTotal: 0,
  publicSearchPages: 1,
};

export function createUserPublicCatalogActions(set, get) {
  /** Public list fetch. `Authorization` is optional: when a learner session
   *  exists it is sent so the DTO's `viewer` flags are populated. */
  async function fetchList(prefix, path, params = {}, keys = []) {
    set({ [`${prefix}Loading`]: true, [`${prefix}Error`]: null });
    try {
      const query = new URLSearchParams(params).toString();
      const data = await apiRequest(get, `${path}${query ? `?${query}` : ""}`);
      const { items, total, totalPages } = normalizeList(data, keys);
      set({
        [`${prefix}Loading`]: false,
        [prefix]: items,
        [`${prefix}Total`]: total,
        [`${prefix}Pages`]: totalPages,
      });
      return { success: true, items, total, totalPages };
    } catch (err) {
      const msg = toDisplayString(err.message, "Request failed");
      set({ [`${prefix}Loading`]: false, [`${prefix}Error`]: msg });
      return { success: false, error: msg };
    }
  }

  async function fetchDetail(prefix, path, keys = []) {
    set({ [`${prefix}Loading`]: true, [`${prefix}Error`]: null });
    try {
      const data = await apiRequest(get, path);
      const payload =
        data?.data ?? (keys.length ? keys.reduce((a, k) => a ?? data?.[k], undefined) : undefined) ?? data;
      set({ [`${prefix}Loading`]: false, [prefix]: payload });
      return { success: true, data: payload };
    } catch (err) {
      const msg = toDisplayString(err.message, "Request failed");
      set({ [`${prefix}Loading`]: false, [`${prefix}Error`]: msg });
      return { success: false, error: msg };
    }
  }

  return {
    // GET /api/public/catalog/topics
    fetchPublicTopics: () =>
      fetchList("publicTopics", "/api/public/catalog/topics", {}, ["topics"]),

    // GET /api/public/catalog/departments
    fetchPublicDepartments: () =>
      fetchList("publicDepartments", "/api/public/catalog/departments", {}, ["departments"]),

    // GET /api/public/catalog/categories
    fetchPublicCategories: () =>
      fetchList("publicCategories", "/api/public/catalog/categories", {}, ["categories"]),

    // GET /api/public/catalog/books
    fetchPublicBooks: (params = {}) =>
      fetchList("publicBooks", "/api/public/catalog/books", params, ["books"]),

    // GET /api/public/catalog/books/:id
    fetchPublicBook: (id) =>
      fetchDetail("publicBook", `/api/public/catalog/books/${id}`, ["book"]),

    // GET /api/public/catalog/courses
    fetchPublicCourses: (params = {}) =>
      fetchList("publicCourses", "/api/public/catalog/courses", params, ["courses"]),

    // GET /api/public/catalog/courses?sort=popular — dedicated trending rail.
    fetchPublicTrendingCourses: () =>
      fetchList("publicTrendingCourses", "/api/public/catalog/courses", {page: 1, limit: 100, sort: "popular"}, ["courses"]),

    // GET /api/public/catalog/courses/:id
    fetchPublicCourse: (id) =>
      fetchDetail("publicCourse", `/api/public/catalog/courses/${id}`, ["course"]),

    // GET /api/public/catalog/courses/:idOrSlug/reviews
    fetchPublicCourseReviews: (idOrSlug, params = {}) =>
      fetchList(
        "publicCourseReviews",
        `/api/public/catalog/courses/${encodeURIComponent(idOrSlug)}/reviews`,
        params,
        ["reviews"]
      ),

    // GET /api/public/catalog/courses/:idOrSlug/lessons/:lessonId/preview
    // Returns the preview payload directly (not stored) — it is per-lesson.
    fetchPublicLessonPreview: async (idOrSlug, lessonId) => {
      try {
        const data = await apiRequest(
          get,
          `/api/public/catalog/courses/${encodeURIComponent(idOrSlug)}/lessons/${encodeURIComponent(lessonId)}/preview`
        );
        return { success: true, data: data?.data ?? data };
      } catch (err) {
        return {
          success: false,
          error: toDisplayString(err.message, "Could not load this preview lesson."),
        };
      }
    },

    // GET /api/public/catalog/journals
    fetchPublicJournals: (params = {}) =>
      fetchList("publicJournals", "/api/public/catalog/journals", params, ["journals"]),

    // GET /api/public/catalog/journals/:id
    fetchPublicJournal: (id) =>
      fetchDetail("publicJournal", `/api/public/catalog/journals/${id}`, ["journal"]),

    // GET /api/public/catalog/resources
    // Mixed marketplace feed: project topics, seminar topics, research
    // materials, manuals, books and journals. The response is paginated
    // ({ items, total, page, limit, totalPages }).
    fetchPublicResources: (params = {}) =>
      fetchList("publicResources", "/api/public/catalog/resources", params, ["resources"]),

    // GET /api/public/catalog/resources/:type/:idOrSlug
    // Returns the detail payload directly (not stored) — it is per-material.
    fetchPublicResourceDetail: async (type, idOrSlug) => {
      try {
        const data = await apiRequest(
          get,
          `/api/public/catalog/resources/${encodeURIComponent(type)}/${encodeURIComponent(idOrSlug)}`
        );
        return { success: true, data: data?.data ?? data };
      } catch (err) {
        return {
          success: false,
          error: toDisplayString(err.message, "Could not load this material."),
        };
      }
    },

    // GET /api/public/catalog/resources/:type/:idOrSlug/preview
    // Returns { id, title, chapterOnePreview } directly (not stored).
    fetchPublicResourcePreview: async (type, idOrSlug) => {
      try {
        const data = await apiRequest(
          get,
          `/api/public/catalog/resources/${encodeURIComponent(type)}/${encodeURIComponent(idOrSlug)}/preview`
        );
        return { success: true, data: data?.data ?? data };
      } catch (err) {
        return {
          success: false,
          error: toDisplayString(err.message, "Could not load the chapter preview."),
        };
      }
    },

    // GET /api/public/catalog/learning-paths
    fetchPublicLearningPaths: (params = {}) =>
      fetchList("publicLearningPaths", "/api/public/catalog/learning-paths", params, [
        "learningPaths",
        "paths",
      ]),

    // GET /api/public/catalog/learning-paths/:id
    fetchPublicLearningPath: (id) =>
      fetchDetail("publicLearningPath", `/api/public/catalog/learning-paths/${id}`, [
        "learningPath",
        "path",
      ]),

    // GET /api/public/catalog/search?q=…
    fetchPublicSearch: (params = {}) =>
      fetchList("publicSearchResults", "/api/public/catalog/search", params, [
        "courses",
        "books",
        "journals",
        "results",
      ]),
  };
}

const userPublicCatalogActions = createUserPublicCatalogActions;
export default userPublicCatalogActions;
